import React, { useMemo, useState } from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import UserLayout from '../../../Layouts/UserLayout'
import TagSelector from '../../../components/TagSelector'

const LANGUAGES = [
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'en', name: 'English' },
]

export default function Edit({ text, tags = [] }) {
  const { auth } = usePage().props
  const userLocale = auth?.user?.native_language || 'en'
  
  const { data, setData, put, processing, errors } = useForm({
    title: text.title,
    tag_ids: text.tags ? text.tags.map(t => t.id) : [],
    difficulty: text.difficulty,
    language: text.language || 'fr',
    content_raw: text.content_raw,
    image_url: text.image_url || '',
    image_file: null,
    tokens: (text.tokens || []).map(t => ({ ...t, t: t.t || '' })),
    spans: text.spans || []
  })

  const [editingTokenId, setEditingTokenId] = useState(null)
  const [selectedSpanTokenIds, setSelectedSpanTokenIds] = useState([])

  function submit(e) {
    e.preventDefault()
    put(`/admin/texts/${text.id}`)
  }

  function handleTokenChange(id, field, value) {
    const newTokens = data.tokens.map(t => {
      if (t.id === id) {
        return { ...t, [field]: value }
      }
      return t
    })
    setData('tokens', newTokens)
  }

  function handleSpanChange(spanIndex, field, value) {
    const next = [...(data.spans || [])]
    next[spanIndex] = { ...(next[spanIndex] || {}), [field]: value }
    setData('spans', next)
  }

  function handleSpanGlossChange(spanIndex, locale, value) {
    const next = [...(data.spans || [])]
    const span = next[spanIndex] || {}
    const gloss = { ...(span.gloss || {}) }
    gloss[locale] = value
    next[spanIndex] = { ...span, gloss }
    setData('spans', next)
  }

  function removeSpanGlossLocale(spanIndex, locale) {
    const next = [...(data.spans || [])]
    const span = next[spanIndex] || {}
    const gloss = { ...(span.gloss || {}) }
    delete gloss[locale]
    next[spanIndex] = { ...span, gloss }
    setData('spans', next)
  }

  function addSpan() {
    const tokenIds = selectedSpanTokenIds.slice().sort((a, b) => a - b)
    if (tokenIds.length < 2) return

    const nextSpans = [...(data.spans || [])]
    nextSpans.push({
      id: nextSpans.length,
      token_ids: tokenIds,
      surface: buildSurfaceFromTokenIds(data.tokens, tokenIds),
      gloss: { [userLocale]: '' },
      lemma: '',
      pos: '',
      notes: ''
    })
    setData('spans', nextSpans)
    setSelectedSpanTokenIds([])
  }

  function removeSpan(spanIndex) {
    const next = [...(data.spans || [])]
    next.splice(spanIndex, 1)
    setData('spans', next)
    setSelectedSpanTokenIds([])
  }

  const spanByTokenId = useMemo(() => {
    const map = new Map()
    for (const span of data.spans || []) {
      for (const tokenId of span.token_ids || []) {
        if (!map.has(tokenId)) map.set(tokenId, span)
      }
    }
    return map
  }, [data.spans])

  const selectedIsValidPhrase = useMemo(() => {
    if (!selectedSpanTokenIds || selectedSpanTokenIds.length < 2) return false
    return isContiguousWordRun(data.tokens, selectedSpanTokenIds)
  }, [data.tokens, selectedSpanTokenIds])

  function toggleSpanSelectionForToken(token) {
    if (token.p) return
    // v1: non-overlapping spans
    if (spanByTokenId.get(token.id)) return

    const next = new Set(selectedSpanTokenIds)
    if (next.has(token.id)) next.delete(token.id)
    else next.add(token.id)

    const arr = Array.from(next)
    // Keep selection sane: if it becomes non-contiguous, reset to just the clicked token.
    if (arr.length > 1 && !isContiguousWordRun(data.tokens, arr)) {
      setSelectedSpanTokenIds([token.id])
      return
    }
    setSelectedSpanTokenIds(arr)
  }

  return (
    <UserLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Edit Text</h1>
          <Link href="/admin/texts" className="text-gray-600 hover:text-gray-900">Cancel</Link>
        </div>
        
        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Metadata Column */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
              <h2 className="font-semibold text-lg border-b pb-2">Metadata</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input 
                  type="text" 
                  value={data.title} 
                  onChange={e => setData('title', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                />
                {errors.title && <div className="text-red-500 text-sm">{errors.title}</div>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Language</label>
                <select 
                  value={data.language} 
                  onChange={e => setData('language', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <TagSelector
                  tags={tags}
                  selectedTagIds={data.tag_ids || []}
                  onChange={(tagIds) => setData('tag_ids', tagIds)}
                  locale={userLocale}
                />
                {errors.tag_ids && <div className="text-red-500 text-sm mt-1">{errors.tag_ids}</div>}
                {errors.tags && <div className="text-red-500 text-sm mt-1">{errors.tags}</div>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Difficulty</label>
                <input 
                  type="number" 
                  min="1" max="5"
                  value={data.difficulty} 
                  onChange={e => setData('difficulty', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                />
              </div>

              <div className="pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image</label>
                {data.image_url ? (
                   <div className="space-y-2">
                     <img src={data.image_url} alt="Cover" className="h-32 w-full object-cover rounded-md" />
                     <button 
                       type="button"
                       onClick={() => setData('image_url', '')}
                       className="text-xs text-red-600 hover:text-red-800 underline"
                     >
                       Remove Image
                     </button>
                   </div>
                 ) : (
                   <div className="space-y-2">
                     <input 
                       type="file" 
                       accept="image/*"
                       onChange={e => setData('image_file', e.target.files[0])}
                       className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                     />
                     <div className="text-center text-gray-400 text-[10px]">- OR -</div>
                     <input 
                       type="text" 
                       placeholder="Paste URL..."
                       value={data.image_url}
                       onChange={e => setData('image_url', e.target.value)}
                       className="w-full rounded-md border-gray-300 shadow-sm border p-2 text-xs"
                     />
                   </div>
                 )}
              </div>

              <div className="pt-4">
                 <button 
                  type="submit" 
                  disabled={processing}
                  className="w-full bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:opacity-50"
                >
                  {processing ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h2 className="font-semibold text-lg border-b pb-2 mb-4">Raw Content</h2>
              <textarea 
                rows="10"
                value={data.content_raw} 
                onChange={e => setData('content_raw', e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono text-sm"
                disabled // Disable editing raw content here to prevent sync issues for now
              />
              <p className="text-xs text-gray-500 mt-2">Raw content cannot be edited directly to preserve token mapping.</p>
            </div>
          </div>

          {/* Tokens Editor Column */}
          <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="font-semibold text-lg border-b pb-2 mb-4">Translation Editor</h2>
            <p className="text-sm text-gray-500 mb-4">Click on any word to edit its translation.</p>

            <div className="space-y-4">
              {/* Preview / Interactive Editor */}
              <div className="p-4 bg-gray-50 rounded-lg border leading-loose text-lg">
                {data.tokens.map(token => {
                  if (token.p) return <span key={token.id} className="mr-1">{token.w}</span>
                  
                  const isEditing = editingTokenId === token.id
                  
                  return (
                    <span key={token.id} className="relative inline-block mx-0.5">
                      {isEditing ? (
                        <div className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-1 bg-white shadow-xl border rounded p-2 w-48">
                          <input 
                            autoFocus
                            type="text" 
                            value={token.t || ''} 
                            onChange={e => handleTokenChange(token.id, 't', e.target.value)}
                            onBlur={() => setEditingTokenId(null)}
                            onKeyDown={e => {
                              if(e.key === 'Enter') setEditingTokenId(null)
                            }}
                            className="w-full border rounded p-1 text-sm text-emerald-700 font-bold"
                            placeholder="Translation..."
                          />
                        </div>
                      ) : null}
                      
                      <span 
                        onClick={() => setEditingTokenId(token.id)}
                        className={`cursor-pointer rounded px-1 ${
                          !token.t
                            ? 'bg-rose-100 text-rose-800' // Highlight untranslated/dummy
                            : 'bg-emerald-50 text-gray-900 hover:bg-emerald-100'
                        }`}
                      >
                        {token.w}
                        <span className="block text-[10px] text-emerald-600 font-bold text-center leading-none -mt-1 h-3 overflow-hidden">
                          {token.t}
                        </span>
                      </span>
                    </span>
                  )
                })}
              </div>

              {/* Bulk List View (optional, maybe clearer for some) */}
              <div className="mt-8 border-t pt-6">
                <h3 className="font-medium mb-4">Bulk Edit</h3>
                <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto pr-2">
                  {data.tokens.filter(t => !t.p).map(token => (
                    <div key={token.id} className="flex items-center gap-2 text-sm border-b pb-2">
                      <span className="w-1/3 font-medium text-gray-700 truncate" title={token.w}>{token.w}</span>
                      <input 
                        type="text" 
                        value={token.t || ''} 
                        onChange={e => handleTokenChange(token.id, 't', e.target.value)}
                        className="flex-1 border rounded px-2 py-1 text-emerald-700"
                        placeholder="Translation"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Phrase Spans Editor */}
              <div className="mt-10 border-t pt-6">
                <h3 className="font-semibold mb-2">Phrase Spans</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Select 2+ contiguous words to create a phrase span (non-overlapping). Phrase gloss is used by the reader; legacy per-word <code className="font-mono">t</code> remains a fallback.
                </p>

                {/* Selection preview */}
                <div className="p-4 bg-gray-50 rounded-lg border leading-loose text-lg">
                  {data.tokens.map(token => {
                    if (token.p) return <span key={`span-${token.id}`} className="mr-1">{token.w}</span>

                    const span = spanByTokenId.get(token.id)
                    const inExistingSpan = !!span
                    const isSelected = selectedSpanTokenIds.includes(token.id)

                    return (
                      <button
                        type="button"
                        key={`span-${token.id}`}
                        onClick={() => toggleSpanSelectionForToken(token)}
                        className={[
                          "inline-block mx-0.5 px-1 rounded transition-colors",
                          inExistingSpan ? "bg-sky-100 text-sky-900 cursor-not-allowed" : "cursor-pointer hover:bg-gray-200",
                          isSelected ? "bg-amber-200 text-gray-900" : ""
                        ].join(" ")}
                        title={inExistingSpan ? `In span: ${span.surface || ''}` : "Click to select for a new phrase"}
                        disabled={inExistingSpan}
                      >
                        {token.w}
                      </button>
                    )
                  })}
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <button
                    type="button"
                    onClick={addSpan}
                    disabled={!selectedIsValidPhrase}
                    className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Create span from selection
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedSpanTokenIds([])}
                    className="text-gray-600 hover:text-gray-900 underline text-sm"
                  >
                    Clear selection
                  </button>
                  {!selectedIsValidPhrase && selectedSpanTokenIds.length > 0 && (
                    <span className="text-xs text-rose-600">
                      Selection must be 2+ contiguous words (no gaps in between word tokens).
                    </span>
                  )}
                </div>

                {/* Existing spans list */}
                <div className="mt-6 space-y-4">
                  {(data.spans || []).length === 0 && (
                    <p className="text-sm text-gray-500 italic">No spans yet.</p>
                  )}

                  {(data.spans || []).map((span, idx) => {
                    const gloss = span.gloss || {}
                    const glossLocales = Object.keys(gloss).sort()
                    const primaryLocale = userLocale
                    const hasPrimary = Object.prototype.hasOwnProperty.call(gloss, primaryLocale)

                    return (
                      <div key={span.id ?? idx} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="text-sm text-gray-500">Surface</div>
                            <div className="font-semibold">{span.surface || buildSurfaceFromTokenIds(data.tokens, span.token_ids || [])}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              token_ids: {(span.token_ids || []).join(", ")}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSpan(idx)}
                            className="text-sm text-rose-600 hover:text-rose-800 underline"
                          >
                            Remove
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Lemma (optional)</label>
                            <input
                              type="text"
                              value={span.lemma || ''}
                              onChange={e => handleSpanChange(idx, 'lemma', e.target.value)}
                              className="mt-1 w-full border rounded px-2 py-1 text-sm"
                              placeholder="e.g. auf jeden Fall"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Label / POS (optional)</label>
                            <input
                              type="text"
                              value={span.pos || ''}
                              onChange={e => handleSpanChange(idx, 'pos', e.target.value)}
                              className="mt-1 w-full border rounded px-2 py-1 text-sm"
                              placeholder="e.g. VP, PP, IDIOM"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Notes (optional)</label>
                            <input
                              type="text"
                              value={span.notes || ''}
                              onChange={e => handleSpanChange(idx, 'notes', e.target.value)}
                              className="mt-1 w-full border rounded px-2 py-1 text-sm"
                              placeholder="Short learner note"
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-gray-700">Gloss (by locale)</div>
                            <button
                              type="button"
                              onClick={() => {
                                const locale = prompt('Add locale code (en, fr, es, de, it, pt, ru, ja, zh):')
                                if (!locale) return
                                const code = locale.trim().toLowerCase()
                                if (!code) return
                                handleSpanGlossChange(idx, code, gloss[code] || '')
                              }}
                              className="text-sm text-emerald-700 hover:text-emerald-900 underline"
                            >
                              Add locale
                            </button>
                          </div>

                          {!hasPrimary && (
                            <div className="mt-2 text-xs text-rose-600">
                              Tip: add <b>{primaryLocale}</b> gloss — the reader will prefer it for this admin’s locale.
                            </div>
                          )}

                          <div className="mt-3 space-y-2">
                            {(glossLocales.length ? glossLocales : [primaryLocale]).map(locale => (
                              <div key={`${span.id ?? idx}-gloss-${locale}`} className="flex items-center gap-2">
                                <span className="w-10 text-xs font-mono text-gray-600">{locale}</span>
                                <input
                                  type="text"
                                  value={(gloss[locale] || '')}
                                  onChange={e => handleSpanGlossChange(idx, locale, e.target.value)}
                                  className="flex-1 border rounded px-2 py-1 text-sm text-emerald-800 font-semibold"
                                  placeholder={`Translation in ${locale}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeSpanGlossLocale(idx, locale)}
                                  className="text-xs text-gray-500 hover:text-rose-700"
                                  title="Remove locale"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </UserLayout>
  )
}

function buildSurfaceFromTokenIds(tokens, tokenIds) {
  if (!tokenIds || tokenIds.length === 0) return ''
  const ids = tokenIds.slice().sort((a, b) => a - b)
  const minId = ids[0]
  const maxId = ids[ids.length - 1]
  return (tokens || [])
    .filter(t => typeof t.id === 'number' && t.id >= minId && t.id <= maxId)
    .map(t => t.w || '')
    .join('')
    .trim()
}

// A "contiguous word run" means: between the earliest and latest selected word tokens,
// there are no unselected word tokens (punctuation/whitespace tokens are ignored).
function isContiguousWordRun(tokens, selectedTokenIds) {
  const selected = new Set(selectedTokenIds)
  const wordIndices = (tokens || [])
    .map((t, idx) => ({ t, idx }))
    .filter(({ t }) => !t.p && selected.has(t.id))
    .map(({ idx }) => idx)
    .sort((a, b) => a - b)

  if (wordIndices.length < 2) return false

  const start = wordIndices[0]
  const end = wordIndices[wordIndices.length - 1]

  for (let i = start; i <= end; i++) {
    const tok = tokens[i]
    if (!tok) continue
    if (tok.p) continue
    if (!selected.has(tok.id)) return false
  }
  return true
}

